/**
 * useLegendData Hook
 * Aggregates unique symbols from fixtures, controls, and LV products
 * for display in the floor plan legend
 */

import { useMemo, useState, useEffect } from 'react';
import {
  categoryTemplateService,
  type CategoryTemplate,
  type DomainType,
} from '@/services/categoryTemplateService';
import { organizationCategoryService } from '@/features/product-library/services/organizationCategoryService';
import type { LightingFixture, ControlType } from '@/types/floorPlanViewer';
import type { LVProduct } from '@/features/product-library/types';

/**
 * Generate abbreviation-based SVG icon (matches LVProductLayer.tsx)
 */
function generateAbbreviationSvg(
  abbreviation: string,
  fillColor: string,
  borderColor: string,
  textColor: string,
  shape: 'square' | 'circle' = 'square',
): string {
  const shapeElement =
    shape === 'circle'
      ? `<circle cx="12" cy="12" r="10" fill="${fillColor}" stroke="${borderColor}" stroke-width="2"/>`
      : `<rect x="2" y="2" width="20" height="20" rx="2" fill="${fillColor}" stroke="${borderColor}" stroke-width="2"/>`;

  return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${shapeElement}<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="10" font-weight="600" fill="${textColor}">${abbreviation}</text></svg>`;
}

export interface LegendItem {
  key: string;
  displayName: string;
  iconSvg: string | null;
  iconColor: string;
  iconType?: 'svg' | 'abbreviation';
  abbreviation?: string;
  fillColor?: string;
  textColor?: string;
  iconShape?: 'square' | 'circle';
}

export interface LegendCategory {
  name: string;
  domain: DomainType;
  items: LegendItem[];
}

interface UseLegendDataResult {
  categories: LegendCategory[];
  isLoading: boolean;
  isEmpty: boolean;
}

// Domain to display name mapping
const DOMAIN_DISPLAY_NAMES: Record<DomainType, string> = {
  lighting_fixtures: 'LIGHTING',
  lighting_controls: 'CONTROLS',
  lv_products: 'AUDIO / VIDEO / NETWORK',
};

// Order for displaying categories
const DOMAIN_ORDER: DomainType[] = ['lighting_fixtures', 'lighting_controls', 'lv_products'];

/**
 * Extract unique category keys from placed elements
 */
function extractUniqueKeys(
  fixtures: LightingFixture[],
  controls: ControlType[],
  lvProducts: LVProduct[],
): Set<string> {
  const keys = new Set<string>();

  // Extract from fixtures (skip strip/linear fixtures — they are markers without attached products)
  fixtures.forEach(fixture => {
    if (fixture.fixture_form_factor === 'linear') return;
    if (fixture.category) {
      const key = fixture.subcategory
        ? `lighting_fixtures:${fixture.category}:${fixture.subcategory}`
        : `lighting_fixtures:${fixture.category}:`;
      keys.add(key);
    }
  });

  // Extract from controls
  // In the database, controls use category='control' and subcategory=type (keypad, dimmer, etc.)
  controls.forEach(control => {
    // Check for category_info first (from Portal imports)
    if (control.category_info?.category) {
      const key = control.category_info.subcategory
        ? `lighting_controls:${control.category_info.category}:${control.category_info.subcategory}`
        : `lighting_controls:${control.category_info.category}:`;
      keys.add(key);
    } else if (control.type) {
      // Controls are stored with category='control' and type as subcategory
      keys.add(`lighting_controls:control:${control.type}`);
    }
  });

  // Extract from LV products
  lvProducts.forEach(product => {
    // Check category_info first
    if (product.category_info?.category) {
      const key = product.category_info.subcategory
        ? `lv_products:${product.category_info.category}:${product.category_info.subcategory}`
        : `lv_products:${product.category_info.category}:`;
      keys.add(key);
    } else if (product.category) {
      const key = product.subcategory
        ? `lv_products:${product.category}:${product.subcategory}`
        : `lv_products:${product.category}:`;
      keys.add(key);
    }
  });

  return keys;
}

/**
 * Parse a key string into domain, category, subcategory
 */
function parseKey(key: string): { domain: DomainType; category: string; subcategory?: string } {
  const [domain, category, subcategory] = key.split(':');
  return {
    domain: domain as DomainType,
    category,
    subcategory: subcategory || undefined,
  };
}

/**
 * Hook to aggregate unique symbols for the legend
 */
export function useLegendData(
  fixtures: LightingFixture[],
  controls: ControlType[],
  lvProducts: LVProduct[],
): UseLegendDataResult {
  const [templates, setTemplates] = useState<Map<string, CategoryTemplate>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  // Extract unique keys from all placed elements
  const uniqueKeys = useMemo(
    () => extractUniqueKeys(fixtures, controls, lvProducts),
    [fixtures, controls, lvProducts],
  );

  // Fetch templates for all unique keys
  // Uses "stale-while-revalidate" pattern: keep showing existing templates while fetching new ones
  useEffect(() => {
    const fetchTemplates = async () => {
      if (uniqueKeys.size === 0) {
        setTemplates(new Map());
        setIsLoading(false);
        return;
      }

      // Only show loading state on INITIAL load (when we have no templates yet)
      // Otherwise keep showing existing templates while fetching new ones
      const hasExistingTemplates = templates.size > 0;
      if (!hasExistingTemplates) {
        setIsLoading(true);
      }

      // Start with existing templates to preserve loaded data
      const newTemplates = new Map<string, CategoryTemplate>(templates);

      // Only fetch templates we don't already have (optimization)
      const keysToFetch = Array.from(uniqueKeys).filter(key => !newTemplates.has(key));

      if (keysToFetch.length > 0) {
        // Fetch new templates in parallel
        // Check organization categories first, then fall back to system templates
        const fetchPromises = keysToFetch.map(async key => {
          const { domain, category, subcategory } = parseKey(key);

          // First try organization categories (custom categories)
          try {
            const orgCategory = await organizationCategoryService.getByPath(
              domain,
              category,
              subcategory || null,
            );

            if (orgCategory) {
              // Convert org category to CategoryTemplate-like structure
              // Generate SVG if it's an abbreviation-based icon
              const iconSvg =
                orgCategory.icon_svg ||
                (orgCategory.abbreviation
                  ? generateAbbreviationSvg(
                      orgCategory.abbreviation,
                      orgCategory.fill_color || '#6B7280',
                      orgCategory.border_color || orgCategory.fill_color || '#374151',
                      orgCategory.text_color || '#FFFFFF',
                      orgCategory.icon_shape || 'square',
                    )
                  : null);

              const template: CategoryTemplate = {
                id: orgCategory.id,
                domain: orgCategory.domain as DomainType,
                category: orgCategory.category,
                subcategory: orgCategory.subcategory || null,
                display_name:
                  orgCategory.display_name || orgCategory.subcategory || orgCategory.category,
                abbreviation: orgCategory.abbreviation || null,
                icon_type: orgCategory.icon_svg
                  ? 'svg'
                  : orgCategory.abbreviation
                    ? 'abbreviation'
                    : 'svg',
                icon_svg: iconSvg,
                default_color: orgCategory.fill_color || orgCategory.default_color || '#6B7280',
                fill_color: orgCategory.fill_color || null,
                text_color: orgCategory.text_color || null,
                icon_shape: orgCategory.icon_shape || null,
                display_order: orgCategory.display_order ?? 0,
                is_active: true,
                created_at: orgCategory.created_at || new Date().toISOString(),
                updated_at: orgCategory.updated_at || new Date().toISOString(),
              };
              newTemplates.set(key, template);
              return;
            }
          } catch {
            // Organization categories may not be available (e.g., unauthenticated)
          }

          // Fall back to system templates
          try {
            const template = await categoryTemplateService.getByPath(domain, category, subcategory);
            if (template) {
              newTemplates.set(key, template);
            }
          } catch {
            // Template not found, skip silently
          }
        });

        await Promise.all(fetchPromises);
      }

      // Remove templates for keys that no longer exist (element was deleted)
      newTemplates.forEach((_, key) => {
        if (!uniqueKeys.has(key)) {
          newTemplates.delete(key);
        }
      });

      setTemplates(newTemplates);
      setIsLoading(false);
    };

    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueKeys]); // templates intentionally excluded to avoid infinite loop

  // Build categorized legend data
  const categories = useMemo((): LegendCategory[] => {
    const categoryMap = new Map<DomainType, LegendItem[]>();

    // Initialize empty arrays for each domain
    DOMAIN_ORDER.forEach(domain => {
      categoryMap.set(domain, []);
    });

    // Group items by domain
    templates.forEach((template, key) => {
      const { domain } = parseKey(key);
      const items = categoryMap.get(domain);
      if (items) {
        items.push({
          key,
          displayName: template.display_name,
          iconSvg: template.icon_svg || null,
          iconColor: template.default_color,
          iconType: template.icon_type,
          abbreviation: template.abbreviation,
          fillColor: template.fill_color,
          textColor: template.text_color,
          iconShape: template.icon_shape,
        });
      }
    });

    // Build result array, only including domains with items
    return DOMAIN_ORDER.filter(domain => (categoryMap.get(domain)?.length ?? 0) > 0).map(
      domain => ({
        name: DOMAIN_DISPLAY_NAMES[domain],
        domain,
        items: categoryMap.get(domain) || [],
      }),
    );
  }, [templates]);

  const isEmpty = categories.length === 0;

  return {
    categories,
    isLoading,
    isEmpty,
  };
}
